import { useState, useCallback } from "react";
import { Building2, Scale, Calculator, FileText, Save, Upload } from "lucide-react";
import DatePickerField from "../../components/common/DatePickerField";
import { useToast } from "../Context/ToastContext";
import TdsField from "./components/TdsField";
import TdsToggle from "./components/TdsToggle";
import TdsFormulaCard from "./components/TdsFormulaCard";
import TdsFeedbackBanner from "./components/TdsFeedbackBanner";
import { INDIAN_STATES, TAX_REGIMES } from "./tdsConstants";
import { loadTdsConfig, saveTdsConfig } from "./tdsStorage";
import { useNavigate } from "react-router-dom";
import { navigateToSettingsHome } from "../../components/settings/settingsExit";

const SECTIONS = [
  { num: 1, title: "Deductor Information", icon: Building2, key: "deductor" },
  { num: 2, title: "Tax Regime Settings", icon: Scale, key: "regime" },
  { num: 3, title: "TDS Calculation Settings", icon: Calculator, key: "calculation" },
  { num: 4, title: "Form 16 Settings", icon: FileText, key: "form16" },
];

export default function TdsConfiguration() {
  const navigate = useNavigate();
  const toast = useToast();
  const [config, setConfig] = useState(loadTdsConfig);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [saveStatus, setSaveStatus] = useState({ type: "idle", text: "Click Save to store your TDS configuration." });

  const update = useCallback((section, field, value) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setFeedback(null);
    setSaveStatus({ type: "idle", text: "You have unsaved changes. Click Save Configuration." });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${section}.${field}`];
      return next;
    });
  }, []);

  const validate = () => {
    const errs = {};
    if (!config.deductor.tanNumber?.trim()) errs["deductor.tanNumber"] = "TAN is required";
    if (!config.deductor.deductorName?.trim()) errs["deductor.deductorName"] = "Deductor name is required";
    if (!config.deductor.state?.trim()) errs["deductor.state"] = "State is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      const msg = "Please fill all required fields: TAN Number, Deductor Name, and State.";
      setFeedback({ type: "error", message: msg });
      setSaveStatus({ type: "error", text: msg });
      toast.error(msg);
      return;
    }
    saveTdsConfig(config);
    const time = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    const msg = `TDS configuration saved successfully at ${time}.`;
    setFeedback({ type: "success", message: msg });
    setSaveStatus({ type: "success", text: msg });
    toast.success("TDS configuration saved successfully.");
    navigateToSettingsHome(navigate, { replace: true });
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowed.includes(file.type)) {
      const msg = "Only JPG, JPEG, or PNG files are allowed for digital signature.";
      setErrors((prev) => ({ ...prev, signature: msg }));
      setFeedback({ type: "error", message: msg });
      toast.error(msg);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setConfig((prev) => {
        const next = {
          ...prev,
          form16: {
            ...prev.form16,
            digitalSignature: reader.result,
            digitalSignatureName: file.name,
          },
        };
        return next;
      });
      setSaveStatus({ type: "idle", text: "Signature uploaded. Click Save Configuration to apply." });
      toast.success(`Digital signature "${file.name}" uploaded. Click Save to store.`);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="tds-page-content space-y-5">
      <TdsFeedbackBanner
        message={feedback?.message}
        type={feedback?.type}
        onClose={() => setFeedback(null)}
      />

      {/* Section 1 */}
      <section className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">
              <span className="tds-section-num">{SECTIONS[0].num}</span>
              {SECTIONS[0].title}
            </h2>
            <p className="tds-panel-sub">
              Employer (deductor) details for Form 16, TDS reports & quarterly returns
            </p>
          </div>
        </div>
        <div className="tds-panel-body">
          <div className="tds-grid-2">
            <TdsField label="TAN Number" required error={errors["deductor.tanNumber"]}>
              <input
                value={config.deductor.tanNumber}
                onChange={(e) => update("deductor", "tanNumber", e.target.value.toUpperCase())}
                placeholder="e.g. DELA12345A"
                maxLength={10}
              />
            </TdsField>
            <TdsField label="Deductor Name" required error={errors["deductor.deductorName"]}>
              <input
                value={config.deductor.deductorName}
                onChange={(e) => update("deductor", "deductorName", e.target.value)}
                placeholder="Company display name"
              />
            </TdsField>
            <TdsField label="Legal Entity Name">
              <input
                value={config.deductor.legalEntityName}
                onChange={(e) => update("deductor", "legalEntityName", e.target.value)}
                placeholder="Registered legal name"
              />
            </TdsField>
            <TdsField label="State" required error={errors["deductor.state"]}>
              <select
                value={config.deductor.state}
                onChange={(e) => update("deductor", "state", e.target.value)}
              >
                <option value="">Select state</option>
                {INDIAN_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </TdsField>
            <TdsField label="Address" className="md:col-span-2">
              <textarea
                rows={2}
                value={config.deductor.address}
                onChange={(e) => update("deductor", "address", e.target.value)}
                placeholder="Full company address"
              />
            </TdsField>
            <TdsField label="PIN Code">
              <input
                value={config.deductor.pinCode}
                onChange={(e) => update("deductor", "pinCode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit PIN"
              />
            </TdsField>
            <TdsField label="Contact Person">
              <input
                value={config.deductor.contactPerson}
                onChange={(e) => update("deductor", "contactPerson", e.target.value)}
                placeholder="Payroll contact name"
              />
            </TdsField>
            <TdsField label="Email">
              <input
                type="email"
                value={config.deductor.email}
                onChange={(e) => update("deductor", "email", e.target.value)}
                placeholder="contact@company.com"
              />
            </TdsField>
            <TdsField label="Mobile">
              <input
                value={config.deductor.mobile}
                onChange={(e) => update("deductor", "mobile", e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-digit mobile"
              />
            </TdsField>
          </div>
        </div>
      </section>

      {/* Section 2 */}
      <section className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">
              <span className="tds-section-num">{SECTIONS[1].num}</span>
              {SECTIONS[1].title}
            </h2>
            <p className="tds-panel-sub">Configure which tax regimes employees can choose</p>
          </div>
        </div>
        <div className="tds-panel-body space-y-3">
          <TdsToggle
            label="Enable Old Tax Regime"
            description="Allow employees to opt for old regime with deductions (80C, 80D, HRA, etc.)"
            checked={config.regime.enableOldRegime}
            onChange={(v) => update("regime", "enableOldRegime", v)}
          />
          <TdsToggle
            label="Enable New Tax Regime"
            description="Default regime with lower slab rates and fewer deductions"
            checked={config.regime.enableNewRegime}
            onChange={(v) => update("regime", "enableNewRegime", v)}
          />
          <div className="tds-grid-2 mt-4">
            <TdsField label="Default Regime for New Employees">
              <select
                value={config.regime.defaultRegime}
                onChange={(e) => update("regime", "defaultRegime", e.target.value)}
              >
                {TAX_REGIMES.filter(
                  (r) =>
                    (r.value === "old" && config.regime.enableOldRegime) ||
                    (r.value === "new" && config.regime.enableNewRegime)
                ).map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </TdsField>
            <TdsField label="Regime Change Deadline">
              <DatePickerField
                hideLabel
                value={config.regime.regimeChangeDeadline}
                onChange={(v) => update("regime", "regimeChangeDeadline", v)}
                placeholder="Last date to switch regime"
              />
            </TdsField>
          </div>
          <TdsToggle
            label="Allow Regime Change"
            description="Employees can switch between old and new regime before the deadline"
            checked={config.regime.allowRegimeChange}
            onChange={(v) => update("regime", "allowRegimeChange", v)}
          />
        </div>
      </section>

      {/* Section 3 */}
      <section className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">
              <span className="tds-section-num">{SECTIONS[2].num}</span>
              {SECTIONS[2].title}
            </h2>
            <p className="tds-panel-sub">Controls how payroll calculates monthly TDS</p>
          </div>
        </div>
        <div className="tds-panel-body">
          <div className="tds-grid-2 gap-5">
            <div className="space-y-3">
              <TdsToggle
                label="Annualized Projection"
                description="Project annual salary from current monthly × remaining months + earned till date"
                checked={config.calculation.annualizedProjection}
                onChange={(v) => update("calculation", "annualizedProjection", v)}
              />
              <TdsToggle
                label="Consider Previous Employer Income"
                description="Include income from previous employer for mid-year joiners"
                checked={config.calculation.considerPreviousEmployer}
                onChange={(v) => update("calculation", "considerPreviousEmployer", v)}
              />
              <TdsToggle
                label="Consider Bonus"
                description="Add bonus as taxable income in TDS calculation"
                checked={config.calculation.considerBonus}
                onChange={(v) => update("calculation", "considerBonus", v)}
              />
              <TdsToggle
                label="Consider Variable Pay"
                description="Include performance bonus, incentives & commission"
                checked={config.calculation.considerVariablePay}
                onChange={(v) => update("calculation", "considerVariablePay", v)}
              />
              <TdsToggle
                label="Consider Arrears"
                description="Add salary arrears to taxable income"
                checked={config.calculation.considerArrears}
                onChange={(v) => update("calculation", "considerArrears", v)}
              />
              <TdsToggle
                label="Auto Recalculate TDS"
                description="Recalculate remaining tax liability during every payroll run"
                checked={config.calculation.autoRecalculateTds}
                onChange={(v) => update("calculation", "autoRecalculateTds", v)}
              />
            </div>
            <TdsFormulaCard title="Annualized Projection Formula">
              <div>
                <span className="tds-formula-highlight">Projected Annual Salary</span>
                <br />
                = Current Monthly Salary × Remaining Months
                <br />
                + Salary Earned Till Date
              </div>
              <div className="mt-4 pt-3 border-t border-violet-200">
                <span className="tds-formula-highlight">Monthly TDS</span>
                <br />
                = Annual Tax Liability ÷ Remaining Months
              </div>
            </TdsFormulaCard>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section className="tds-panel">
        <div className="tds-panel-head">
          <div>
            <h2 className="tds-panel-title">
              <span className="tds-section-num">{SECTIONS[3].num}</span>
              {SECTIONS[3].title}
            </h2>
            <p className="tds-panel-sub">Form 16 certificate issued to employees after year-end</p>
          </div>
        </div>
        <div className="tds-panel-body">
          <div className="tds-grid-2">
            <TdsField label="Authorized Signatory Name">
              <input
                value={config.form16.signatoryName}
                onChange={(e) => update("form16", "signatoryName", e.target.value)}
                placeholder="Signatory full name"
              />
            </TdsField>
            <TdsField label="Designation">
              <input
                value={config.form16.designation}
                onChange={(e) => update("form16", "designation", e.target.value)}
                placeholder="e.g. Director / CFO"
              />
            </TdsField>
            <TdsField label="Place">
              <input
                value={config.form16.place}
                onChange={(e) => update("form16", "place", e.target.value)}
                placeholder="City for Form 16"
              />
            </TdsField>
            <TdsField label="Digital Signature" error={errors.signature}>
              <label className="tds-upload block">
                <input type="file" accept=".jpg,.jpeg,.png" onChange={handleSignatureUpload} />
                <Upload size={24} className="mx-auto text-violet-500 mb-2" />
                <p className="text-sm font-semibold text-slate-700">
                  {config.form16.digitalSignatureName || "Upload signature image"}
                </p>
                <p className="text-xs text-slate-500 mt-1">JPG, JPEG or PNG — required for Form 16</p>
              </label>
              {config.form16.digitalSignature && (
                <img
                  src={config.form16.digitalSignature}
                  alt="Digital signature preview"
                  className="mt-3 max-h-16 rounded border border-slate-200"
                />
              )}
            </TdsField>
          </div>
          <div className="tds-save-bar">
            <p className={`tds-save-status tds-save-status--${saveStatus.type}`}>
              {saveStatus.text}
            </p>
            <button type="button" className="tds-btn tds-btn--primary" onClick={handleSave}>
              <Save size={16} />
              Save Configuration
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
